import * as React from "react";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import { Api, PageSpinner } from "../util";
import { AppInvite } from "../types";
import { AuthContext } from "../context";
import CopyButton from "../components/CopyButton";
import MutedLink from "../components/MutedLink";

const updates = [
  {
    title: "🚗 Rental Car Discounts",
    date: "March 18, 2023",
    content: (
      <>
        We&apos;ve secured rental car discounts for your convenience:
        <ul className="mb-0">
          <li>
            <MutedLink href="https://www.budget.com/">Budget</MutedLink>{" "}
            Customer Discount (BCD) code: <code>S444917</code>{" "}
            <CopyButton text="S444917" />
          </li>
          <li>
            {" "}
            <MutedLink href="https://www.avis.com/">Avis</MutedLink> Customer
            Discount (AWD) code: <code>J546139</code>{" "}
            <CopyButton text="J546139" />
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "🏨 Hotel Blocks",
    date: "January 25, 2023",
    content: (
      <>
        We&apos;re excited to share that we&apos;ve reserved a block of rooms at
        the{" "}
        <MutedLink href="https://goo.gl/maps/syRwNbFHmwTFSuKe6">
          SpringHill Suites Houston Northwest
        </MutedLink>
        . You&apos;ll be able to{" "}
        <MutedLink href="https://www.marriott.com/events/start.mi?id=1674659031643&key=GRP">
          book using the group rate
        </MutedLink>{" "}
        until May 1. We&apos;ve also secured a block at the{" "}
        <MutedLink href="https://www.hilton.com/en/hotels/housuhx-hampton-suites-houston-sugar-land/">
          Hampton Inn & Suites Houston Sugar Land
        </MutedLink>
        , located closer to the reception venue (use group code <code>RCE</code>{" "}
        <CopyButton text="RCE" />
        ). See the{" "}
        <Link className="link-secondary" to="/q-and-a">
          Q&A
        </Link>{" "}
        for more information and other recommendations.
      </>
    ),
  },
  {
    title: "💃 Sangeet Venue",
    date: "January 21, 2023",
    event: "sangeet",
    content: (
      <>
        We&apos;ve finalized our Sangeet venue and updated the{" "}
        <Link className="link-secondary" to="/schedule">
          Schedule
        </Link>{" "}
        to reflect this! We can&apos;t wait to dance with you all at{" "}
        <MutedLink href="https://goo.gl/maps/cDyeJx4UBjrUrnoo7">
          Ashiana Indian Restaurant
        </MutedLink>
        .
      </>
    ),
  },
  {
    title: "🎁 Registry",
    date: "January 18, 2023",
    content: (
      <>
        We&apos;ve published our{" "}
        <Link className="link-secondary" target="_blank" to="/registry">
          Registry
        </Link>
        . We&apos;re registered at Amazon and Crate &amp; Barrel, but you can
        view all the items we&apos;ve selected at the previous link. Please note
        that we will not be able to receive any boxed gifts at the wedding
        venue.
      </>
    ),
  },
  {
    title: "📰 Updates ",
    date: "January 17, 2023",
    content: (
      <>
        In true SWE fashion, we&apos;ve added a changelog so you can easily find
        the latest changes to our website. The{" "}
        <Link className="link-secondary" to="/updates">
          Updates
        </Link>{" "}
        tab is the best place to find the latest changes to any events as well
        as new information about accomodations and logistics. Check back here
        for the latest updates!
      </>
    ),
  },
];

const Updates = () => {
  const [loading, setLoading] = React.useState(false);
  const [invites, setInvites] =
    React.useState<Array<AppInvite["event"]["slug"]>>();

  const { fetch } = React.useContext(AuthContext);

  React.useEffect(() => {
    if (fetch) {
      const api = new Api(fetch);
      void (async () => {
        setInvites((await api.getInvites()).map((invite) => invite.event.slug));
        setLoading(false);
      })();
    }
  }, [fetch]);

  if (loading || (fetch && !invites)) {
    return <PageSpinner />;
  }

  return (
    <Container>
      <Helmet>
        <title>Updates</title>
      </Helmet>

      <div className="d-flex flex-column align-items-center gap-4">
        {updates
          .filter(({ event }) => event == null || invites?.includes(event))
          .map(({ title, date, content }) => (
            <Card className="bg-light py-1 w-100" key={title}>
              <Card.Body>
                <Card.Title className="fs-3">{title}</Card.Title>
                <Card.Subtitle className="text-muted mb-2">
                  {date}
                </Card.Subtitle>
                <Card.Text>{content}</Card.Text>
              </Card.Body>
            </Card>
          ))}
      </div>
    </Container>
  );
};

export default Updates;
